import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import analyticsService, { toParams } from "../../services/analyticsService";

const STORAGE_KEY = "analytics.filters.v1";

export const DEFAULT_FILTERS = {
  preset: "30d",
  startDate: "",
  endDate: "",
  granularity: "", // "" = let the server pick
  teamId: "all",
  recognitionTypeId: "all",
  compare: true,
};

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeStored = (filters) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* storage unavailable: filters simply reset next visit */
  }
};

/**
 * Filter state shared by every panel on a dashboard, remembered per browser.
 */
export const useAnalyticsFilters = (overrides = {}) => {
  const [filters, setFiltersState] = useState(() => ({ ...DEFAULT_FILTERS, ...(readStored() || {}), ...overrides }));
  const setFilters = useCallback((patch) => {
    setFiltersState((prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      writeStored(next);
      return next;
    });
  }, []);
  const reset = useCallback(() => setFilters({ ...DEFAULT_FILTERS, ...overrides }), [setFilters, overrides]);
  return { filters, setFilters, reset };
};

/**
 * One section of the dashboard. The query key includes the resolved params so
 * two panels with the same filters share a cache entry.
 */
export const useAnalyticsSection = (section, filters, options = {}) => {
  const params = useMemo(() => toParams(filters), [filters]);
  const fetcher = {
    filters: analyticsService.getFilters,
    overview: analyticsService.getOverview,
    insights: analyticsService.getInsights,
    timeseries: analyticsService.getTimeseries,
    breakdowns: analyticsService.getBreakdowns,
    participation: analyticsService.getParticipation,
    network: analyticsService.getNetwork,
    leaderboards: (f) => analyticsService.getLeaderboards(f, options.limit || 10),
    points: analyticsService.getPoints,
    nominations: analyticsService.getNominations,
    rewards: analyticsService.getRewards,
    engagement: analyticsService.getEngagement,
  }[section];
  return useQuery({
    queryKey: ["analytics", section, params, options.limit || null],
    queryFn: () => fetcher(filters),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
    enabled: options.enabled !== false,
  });
};
